/**
 * Card view for one owned property on the Properties page. Mirrors the
 * `.prop-card` layout from `design/Syntra/properties.html`:
 *
 *   - Hero photo (200px) with a Status pill (top-left) and Strategy pill
 *     (top-right).
 *   - Address + city/state · bd/ba/sqft.
 *   - Performance label.
 *   - Three metric rows (strategy-specific) — label + badge on the left,
 *     big value + unit on the right.
 *   - Footer: "Owned X days" + "View details →" CTA.
 */
import {
  ArrowLeftRight,
  Calendar,
  ChevronRight,
  Home,
  RefreshCw,
  Wrench,
} from "lucide-react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { Strategy } from "@/types/pipeline";

import type { MetricBadge, MetricTile, PropertyStatus } from "./property-metrics";

interface PropertyCardProps {
  id: string;
  imgUrl?: string | null;
  addr1: string;
  addr2: string;
  status: PropertyStatus;
  strategy: Strategy | null;
  tiles: MetricTile[];
  daysOwned: number;
  onOpen: (id: string) => void;
  /** Show the "Move to Rehab" action — only when the property has no rehab project yet. */
  canMoveToRehab?: boolean;
  /** Fired when "Move to Rehab" is clicked (enrolls the property into rehab). */
  onMoveToRehab?: (id: string) => void;
  /** Disables the "Move to Rehab" button while its mutation is in flight. */
  isMovingToRehab?: boolean;
}

function titleCase(s: string): string {
  return s
    .split(" ")
    .map((word) => {
      if (word.length <= 2 && /^[A-Z]+$/.test(word.replace(/[,.]/g, ""))) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

const STATUS_LABEL: Record<PropertyStatus, string> = {
  in_rehab: "In Rehab",
  owned: "Owned",
  sold: "Sold",
};

const STATUS_DOT: Record<PropertyStatus, string> = {
  in_rehab: "bg-warning shadow-[0_0_0_3px_hsl(var(--warning)/0.18)]",
  owned: "bg-success shadow-[0_0_0_3px_hsl(var(--success)/0.18)]",
  sold: "bg-muted-foreground shadow-[0_0_0_3px_hsl(var(--muted-foreground)/0.18)]",
};

const BADGE_CLASSES: Record<MetricBadge["tone"], string> = {
  good: "bg-success/15 text-success border border-success/25",
  warn: "bg-warning/15 text-warning border border-warning/25",
  bad: "bg-destructive/15 text-destructive border border-destructive/25",
};

const VALUE_TONE: Record<NonNullable<MetricTile["valueTone"]>, string> = {
  pos: "text-success",
  neg: "text-destructive",
  warn: "text-warning",
};

function StrategyPill({ strategy }: { strategy: Strategy }) {
  const Icon =
    strategy === "Flip"
      ? ArrowLeftRight
      : strategy === "Rental"
        ? Home
        : RefreshCw;
  return (
    <span className="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/60 px-2.5 py-1 text-[11.5px] font-semibold text-white backdrop-blur">
      <Icon className="h-3 w-3" aria-hidden />
      {strategy}
    </span>
  );
}

export function PropertyCard({
  id,
  imgUrl,
  addr1,
  addr2,
  status,
  strategy,
  tiles,
  daysOwned,
  onOpen,
  canMoveToRehab = false,
  onMoveToRehab,
  isMovingToRehab = false,
}: PropertyCardProps) {
  return (
    <Card
      onClick={() => onOpen(id)}
      className="flex cursor-pointer flex-col overflow-hidden p-0 transition hover:border-border/80"
    >
      {/* Hero */}
      <div
        className="relative h-[200px] bg-cover bg-center bg-muted/40"
        style={imgUrl ? { backgroundImage: `url('${imgUrl}')` } : undefined}
        aria-hidden
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent to-black/55" />
        <span className="absolute left-3 top-3 z-10 inline-flex items-center gap-2 rounded-full border border-white/20 bg-black/60 px-2.5 py-1 text-[11.5px] font-semibold text-white backdrop-blur">
          <span
            className={cn("h-1.5 w-1.5 rounded-full", STATUS_DOT[status])}
            aria-hidden
          />
          {STATUS_LABEL[status]}
        </span>
        {strategy && <StrategyPill strategy={strategy} />}
      </div>

      {/* Body */}
      <div className="flex flex-col gap-3.5 p-4">
        <div>
          <div className="text-base font-bold leading-tight text-foreground">
            {titleCase(addr1) || "Property"}
          </div>
          {addr2 && (
            <div className="mt-0.5 text-xs text-muted-foreground">{addr2}</div>
          )}
        </div>

        {/* Three strategy metric rows, boxed (design `.pc-perf-list`) */}
        <div className="flex flex-col rounded-[10px] border border-border bg-muted/30 px-3.5 py-1">
          {tiles.map((tile, idx) => (
            <div
              key={tile.key}
              className={cn(
                "flex items-start justify-between gap-3 py-3",
                idx !== 0 && "border-t border-border",
              )}
            >
              <div className="flex min-w-0 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[13px] font-semibold text-foreground">
                    {tile.label}
                  </span>
                  {tile.badge && (
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[10.5px] font-semibold",
                        BADGE_CLASSES[tile.badge.tone],
                      )}
                    >
                      {tile.badge.label}
                    </span>
                  )}
                </div>
                <div className="text-xs tabular-nums text-muted-foreground">
                  {tile.detail}
                </div>
              </div>
              <div className="flex flex-shrink-0 flex-col items-end gap-0.5">
                <div
                  className={cn(
                    "text-lg font-extrabold leading-tight tabular-nums",
                    tile.valueTone ? VALUE_TONE[tile.valueTone] : "text-foreground",
                  )}
                >
                  {tile.value}
                </div>
                <div className="text-[11.5px] tabular-nums text-muted-foreground">
                  {tile.unit}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            {daysOwned > 0
              ? `Owned ${daysOwned} ${daysOwned === 1 ? "day" : "days"}`
              : "Purchased recently"}
          </span>
          <div className="inline-flex items-center gap-2">
            {canMoveToRehab && onMoveToRehab && (
              <button
                type="button"
                disabled={isMovingToRehab}
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToRehab(id);
                }}
                className="inline-flex items-center gap-1.5 rounded-full border border-success/55 bg-success/[0.12] px-3 py-1.5 text-[12px] font-semibold text-success transition hover:border-success hover:bg-success/20 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Wrench className="h-3.5 w-3.5" aria-hidden />
                Move to Rehab
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpen(id);
              }}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 px-3 py-1.5 text-[12px] font-semibold text-foreground transition hover:border-muted-foreground hover:bg-muted"
            >
              View details
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}
